import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea'; // Assuming this exists or I'll use standard textarea
import { X, Plus, Trash, Save, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['Texto', 'Imagem', 'Vídeo', 'Código', 'Áudio', 'Automação', 'Pesquisa', 'Apresentação', 'Multimodal', 'Geral'];

export default function EditToolModal({ tool, onClose, onUpdate, onDelete }) {
    const [formData, setFormData] = useState(JSON.parse(JSON.stringify(tool))); // Deep copy
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general'); // general, details, lists, cases

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleListChange = (field, value) => {
        // Value coming from textarea, split by newline
        const list = value.split('\n').filter(item => item.trim() !== '');
        setFormData(prev => ({ ...prev, [field]: list }));
    };

    const handleUseCaseChange = (index, field, value) => {
        const newCases = [...(formData.use_cases || [])];
        if (!newCases[index]) newCases[index] = {};
        newCases[index][field] = value;
        setFormData(prev => ({ ...prev, use_cases: newCases }));
    };

    const addUseCase = () => {
        setFormData(prev => ({
            ...prev,
            use_cases: [...(prev.use_cases || []), { title: '', bad_prompt: '', good_prompt: '' }]
        }));
    };

    const removeUseCase = (index) => {
        setFormData(prev => ({
            ...prev,
            use_cases: prev.use_cases.filter((_, i) => i !== index)
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API}/admin/ai-tools/${tool.tool_id}`,
                formData,
                { withCredentials: true }
            );
            toast.success('Ferramenta atualizada!');
            onUpdate(formData);
            onClose();
        } catch (error) {
            console.error(error);
            toast.error('Erro ao atualizar ferramenta');
        } finally {
            setSaving(false);
        }
    };

    const [regenerating, setRegenerating] = useState(false);

    const handleRegenerate = async () => {
        if (!window.confirm("ATENÇÃO: Isso irá substituir TODOS os campos atuais por uma nova geração da IA. Suas edições manuais serão perdidas. Continuar?")) {
            return;
        }

        setRegenerating(true);
        try {
            const response = await axios.post(
                `${API}/admin/ai-tools/${tool.tool_id}/regenerate`,
                {},
                { withCredentials: true }
            );
            setFormData(response.data);
            toast.success('Ficha regenerada com sucesso!');
        } catch (error) {
            console.error(error);
            toast.error('Erro ao regenerar ficha');
        } finally {
            setRegenerating(false);
        }
    };
    const [deleting, setDeleting] = useState(false);

    const handleDelete = async () => {
        if (!window.confirm("ATENÇÃO: Tem certeza que deseja DELETAR esta ferramenta? Esta ação não pode ser desfeita.")) {
            return;
        }

        setDeleting(true);
        try {
            await axios.delete(
                `${API}/admin/ai-tools/${tool.tool_id}`,
                { withCredentials: true }
            );
            toast.success('Ferramenta deletada com sucesso!');
            if (onClose) onClose();
            if (onDelete) onDelete();
        } catch (error) {
            console.error(error);
            toast.error('Erro ao deletar ferramenta');
            setDeleting(false);
        }
    };

    const listToString = (list) => (list || []).join('\n');

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-4xl w-full max-h-[90vh] flex flex-col rounded-none shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between p-6 border-b border-border bg-background">
                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Editar Ferramenta: {formData.name}</h2>
                    <Button variant="ghost" size="icon" onClick={onClose}>
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-border bg-muted/20">
                    <button
                        className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'general' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setActiveTab('general')}
                    >
                        Geral
                    </button>
                    <button
                        className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'details' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setActiveTab('details')}
                    >
                        Detalhes
                    </button>
                    <button
                        className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'lists' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setActiveTab('lists')}
                    >
                        Listas (Features, Prós...)
                    </button>
                    <button
                        className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'cases' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                        onClick={() => setActiveTab('cases')}
                    >
                        Casos de Uso
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {activeTab === 'general' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="text-sm font-medium mb-1 block">Nome</label>
                                <Input value={formData.name} onChange={e => handleChange('name', e.target.value)} className="rounded-none" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-sm font-medium mb-1 block">URL</label>
                                <Input value={formData.url || ''} onChange={e => handleChange('url', e.target.value)} className="rounded-none" />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Categoria</label>
                                <select
                                    value={formData.category}
                                    onChange={e => handleChange('category', e.target.value)}
                                    className="w-full h-10 px-3 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                >
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Detalhes de Preço</label>
                                <Input value={formData.pricing_details || ''} onChange={e => handleChange('pricing_details', e.target.value)} className="rounded-none" placeholder="Ex: Free, $20/mo" />
                            </div>
                            <div className="flex items-center gap-2 mt-4">
                                <input
                                    type="checkbox"
                                    id="is_free"
                                    checked={formData.is_free}
                                    onChange={e => handleChange('is_free', e.target.checked)}
                                    className="rounded border-gray-300"
                                />
                                <label htmlFor="is_free" className="text-sm">É Gratuito / Tem plano Free?</label>
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Nível</label>
                                <select
                                    value={formData.level}
                                    onChange={e => handleChange('level', e.target.value)}
                                    className="w-full h-10 px-3 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                >
                                    <option value="iniciante">Iniciante</option>
                                    <option value="intermediario">Intermediário</option>
                                    <option value="avancado">Avançado</option>
                                </select>
                            </div>

                            {/* Regenerate Section */}
                            <div className="col-span-2 mt-4 pt-6 border-t border-border bg-purple-500/5 p-4 -mx-2 sm:mx-0">
                                <h3 className="text-sm font-bold mb-2 flex items-center gap-2 text-purple-600">
                                    <span className="text-lg">✨</span>
                                    Regenerar com IA
                                </h3>
                                <p className="text-xs text-muted-foreground mb-4">
                                    Se a ficha atual estiver incorreta ou alucinada, você pode pedir para a IA gerar tudo novamente do zero.
                                    <br /><span className="font-bold text-red-400">Atenção:</span> Isso substituirá todos os campos atuais.
                                </p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleRegenerate}
                                    disabled={regenerating || deleting}
                                    className="w-full sm:w-auto border-purple-200 hover:bg-purple-100 text-purple-700"
                                >
                                    {regenerating ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <div className="mr-2 h-3 w-3 rotate-0">↻</div>}
                                    Regenerar Ficha Completa
                                </Button>
                            </div>

                            {/* Delete Section */}
                            <div className="col-span-2 mt-4 pt-6 border-t border-border bg-red-500/5 p-4 -mx-2 sm:mx-0">
                                <h3 className="text-sm font-bold mb-2 flex items-center gap-2 text-red-600">
                                    <Trash className="h-4 w-4" />
                                    Zona de Perigo
                                </h3>
                                <p className="text-xs text-muted-foreground mb-4">
                                    Deseja remover esta ferramenta permanentemente da plataforma?
                                </p>
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={handleDelete}
                                    disabled={deleting || regenerating}
                                    className="w-full sm:w-auto"
                                >
                                    {deleting ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : <Trash className="h-3 w-3 mr-2" />}
                                    Excluir Ferramenta
                                </Button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'details' && (
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-medium mb-1 block">Descrição Curta</label>
                                <textarea
                                    value={formData.description || ''}
                                    onChange={e => handleChange('description', e.target.value)}
                                    className="w-full min-h-[80px] p-3 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Como Funciona (Detalhado)</label>
                                <textarea
                                    value={formData.how_it_works || ''}
                                    onChange={e => handleChange('how_it_works', e.target.value)}
                                    className="w-full min-h-[150px] p-3 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'lists' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-medium mb-1 block flex items-center justify-between">
                                    Principais Features <span className="text-xs text-muted-foreground font-normal">(Uma por linha)</span>
                                </label>
                                <textarea
                                    value={listToString(formData.main_features)}
                                    onChange={e => handleListChange('main_features', e.target.value)}
                                    className="w-full min-h-[120px] p-3 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block flex items-center justify-between">
                                    Destaques <span className="text-xs text-muted-foreground font-normal">(Uma por linha)</span>
                                </label>
                                <textarea
                                    value={listToString(formData.highlights)}
                                    onChange={e => handleListChange('highlights', e.target.value)}
                                    className="w-full min-h-[120px] p-3 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block text-green-500 flex items-center justify-between">
                                    Prós <span className="text-xs text-muted-foreground font-normal">(Um por linha)</span>
                                </label>
                                <textarea
                                    value={listToString(formData.pros)}
                                    onChange={e => handleListChange('pros', e.target.value)}
                                    className="w-full min-h-[120px] p-3 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block text-red-500 flex items-center justify-between">
                                    Contras <span className="text-xs text-muted-foreground font-normal">(Um por linha)</span>
                                </label>
                                <textarea
                                    value={listToString(formData.cons)}
                                    onChange={e => handleListChange('cons', e.target.value)}
                                    className="w-full min-h-[120px] p-3 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="text-sm font-medium mb-1 block flex items-center justify-between">
                                    Dicas de Prompt <span className="text-xs text-muted-foreground font-normal">(Uma por linha)</span>
                                </label>
                                <textarea
                                    value={listToString(formData.prompt_tips)}
                                    onChange={e => handleListChange('prompt_tips', e.target.value)}
                                    className="w-full min-h-[80px] p-3 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'cases' && (
                        <div className="space-y-6">
                            {(formData.use_cases || []).map((uc, i) => (
                                <div key={i} className="p-4 border border-border bg-muted/10 rounded-none relative group">
                                    <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button variant="destructive" size="icon" className="h-6 w-6" onClick={() => removeUseCase(i)}>
                                            <Trash className="h-3 w-3" />
                                        </Button>
                                    </div>
                                    <div className="mb-3">
                                        <label className="text-xs uppercase tracking-wide font-medium mb-1 block">Título</label>
                                        <Input
                                            value={uc.title || ''}
                                            onChange={e => handleUseCaseChange(i, 'title', e.target.value)}
                                            className="rounded-none h-8 text-sm"
                                            placeholder="Ex: Criação de conteúdo"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs uppercase tracking-wide font-medium mb-1 block text-red-400">Prompt Ruim</label>
                                            <textarea
                                                value={uc.bad_prompt || ''}
                                                onChange={e => handleUseCaseChange(i, 'bad_prompt', e.target.value)}
                                                className="w-full min-h-[80px] p-2 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs uppercase tracking-wide font-medium mb-1 block text-green-400">Prompt Bom</label>
                                            <textarea
                                                value={uc.good_prompt || ''}
                                                onChange={e => handleUseCaseChange(i, 'good_prompt', e.target.value)}
                                                className="w-full min-h-[80px] p-2 border border-input bg-background/50 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-ring"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <Button onClick={addUseCase} variant="outline" className="w-full rounded-none border-dashed">
                                <Plus className="h-4 w-4 mr-2" /> Adicionar Caso de Uso
                            </Button>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t border-border bg-background flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} className="rounded-none" disabled={saving || deleting || regenerating}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSave} className="rounded-none min-w-[120px]" disabled={saving || deleting || regenerating}>
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Salvar
                    </Button>
                </div>
            </Card>
        </div>
    );
}
